import { Box, Stack, Typography, Skeleton } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { DashboardSectionHeader } from "../DashboardSectionHeader"
import { faCalendar } from "@fortawesome/pro-regular-svg-icons"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { MeetingList } from "./MeetingList"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { RefetchIntervals } from "../../../../../../core/api/refetchIntervals"
import {
    fetchServiceCenterMeetings,
    ServiceCenterHomeQuery,
    ServiceCenterMeetingsResponse,
} from "../../serviceCenterHomeApi"
import { MeetingDrawer } from "../drawer/MettingDrawer"
import { useMemo, useState } from "react"
import { ServiceCenterBooking } from "../../serviceCenterHomeTypes"
import { ErrorBoundary } from "react-error-boundary"
import { isEmpty } from "lodash"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { UserDashboardErrorFallbackCard } from "../../../../../nonAdminUser/reports/home/components/UserDashboardErrorFallbackCard"
import { MeetingDatePicker } from "./MeetingDatePicker"
import { DateTime } from "luxon"
import { FilterSearch } from "../../../../../../shared/components/createOrder/components/FilterSearch"
import { StandardSquareFilter } from "../../../../../../shared/components/filters/squareFilter/StandardSquareFilter"
import { MeetingAudienceFilterType } from "../../../../../../shared/components/filters/orderTypeFilter/helpers"
import { useCurrentServiceCenter } from "../../../../store/serviceCenterHooks"
import { useDateTime } from "../../../../../localization/useDateTime"
import { loadMeetingAudienceFilter, saveMeetingAudienceFilter, toAudienceParam } from "./helpers"

const useStyles = makeStyles()(() => ({
    card: {
        backgroundColor: "white",
        borderRadius: "0.75rem",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
        padding: "1.5rem",
    },
    skeletonCard: {
        height: "2rem",
        marginBottom: ".5rem",
        borderRadius: ".5rem",
    },
    noDataContainer: {
        padding: "2rem 2rem 2rem 0",
    },
}))

const MEETING_AUDIENCE_FILTER_OPTIONS = Object.values(MeetingAudienceFilterType)

function transformMeetings(data: ServiceCenterMeetingsResponse) {
    return data.orders
}

function SafeMeetingSection() {
    const { classes } = useStyles()
    const { serviceCenterId } = useCurrentServiceCenter()
    const [selectedMeeting, setSelectedMeeting] = useState<ServiceCenterBooking | null>(null)
    const [searchQuery, setSearchQuery] = useState("")

    const isMeetingDetailsDrawerOpen = selectedMeeting !== null

    const dateTimeFactory = useDateTime()

    const [selectedDate, setSelectedDate] = useState<DateTime | null>(dateTimeFactory.now())
    const [audienceFilter, setAudienceFilter] = useState(loadMeetingAudienceFilter)

    const handleDateChange = (newDate: DateTime | null) => {
        setSelectedDate(newDate)
    }

    const handleAudienceFilterChange = (filter: MeetingAudienceFilterType) => {
        setAudienceFilter(filter)
        saveMeetingAudienceFilter(filter)
    }

    const { data: meetings, isLoading } = useApiQuery(fetchServiceCenterMeetings, {
        queryName: ServiceCenterHomeQuery.SERVICE_CENTER_MEETINGS,
        dependencies: {
            serviceCenterId: serviceCenterId,
            startDate: selectedDate?.toISODate() ?? "",
            audience: toAudienceParam(audienceFilter),
        },
        select: transformMeetings,
        refetchInterval: RefetchIntervals.short,
    })

    const filteredMeetings = useMemo(() => {
        if (!searchQuery) return meetings
        return meetings?.filter((meeting) =>
            meeting.meetingDetails.meetingName.toLowerCase().includes(searchQuery.toLowerCase()),
        )
    }, [meetings, searchQuery])

    return (
        <Box flex={1}>
            <Box className={classes.card}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom="1rem">
                    <DashboardSectionHeader icon={faCalendar} badgeNumber={filteredMeetings?.length}>
                        <LocalizedStrict id="service-center-reports-dashboard-meeting-title">
                            <Typography variant="h6" fontWeight="bold">
                                Meetings
                            </Typography>
                        </LocalizedStrict>
                    </DashboardSectionHeader>

                    <Stack direction="row" justifyContent="end" alignItems="center" spacing=".5rem">
                        <MeetingDatePicker value={selectedDate} onChange={handleDateChange} />
                        <FilterSearch search={searchQuery} setSearch={setSearchQuery} />
                    </Stack>
                </Stack>

                <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom="1rem">
                    <LocalizedStrict id="service-center-reports-dashboard-meeting-audience-filter-label">
                        <Typography variant="body2" color="text.secondary">
                            Show meetings
                        </Typography>
                    </LocalizedStrict>

                    <StandardSquareFilter
                        value={audienceFilter}
                        onChange={handleAudienceFilterChange}
                        options={MEETING_AUDIENCE_FILTER_OPTIONS}
                        translationBase="standard-order-type-filter-mode"
                    />
                </Stack>

                {isLoading ? (
                    <>
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                    </>
                ) : !isEmpty(filteredMeetings) ? (
                    <ErrorBoundary fallback={<UserDashboardErrorFallbackCard />}>
                        <MeetingList
                            selectedMeeting={selectedMeeting}
                            onMeetingClick={(item) => setSelectedMeeting(item.id === selectedMeeting?.id ? null : item)}
                            meetings={filteredMeetings}
                        />
                    </ErrorBoundary>
                ) : (
                    <NoMeetingsCard />
                )}

                <MeetingDrawer
                    orderId={selectedMeeting?.id}
                    isOpen={isMeetingDetailsDrawerOpen}
                    meetings={meetings}
                    onClose={() => setSelectedMeeting(null)}
                />
            </Box>
        </Box>
    )
}

export function MeetingSection() {
    return (
        <QueryErrorBoundary Fallback={MeetingErrorFallback}>
            <SafeMeetingSection />
        </QueryErrorBoundary>
    )
}

function MeetingErrorFallback() {
    const { classes } = useStyles()
    return (
        <Box flex={1}>
            <Box className={classes.card}>
                <DashboardSectionHeader icon={faCalendar}>
                    <LocalizedStrict id="service-center-reports-dashboard-meeting-title">
                        <Typography variant="h6" fontWeight="bold">
                            Meetings
                        </Typography>
                    </LocalizedStrict>
                </DashboardSectionHeader>
                <NoMeetingsCard />
            </Box>
        </Box>
    )
}

function NoMeetingsCard() {
    const { classes } = useStyles()
    return (
        <Box className={classes.noDataContainer}>
            <LocalizedStrict id="service-center-reports-dashboard-no-meetings">
                <Typography variant="subtitle1" color="#666666">
                    There are no meetings today.
                </Typography>
            </LocalizedStrict>
        </Box>
    )
}
